import { c, card } from '../../theme';
import ExecGauge from './ExecGauge';
import MetricSparkline from './MetricSparkline';
import { fmtCount, fmtMmSs, fmtMoney, fmtPct } from './format';

// The two cards the Executive Overview is built from — a primary driver and a
// supporting metric — laid out the way the old page lays them out.
//
// Both carry the same six things, in the same order, which is what makes the
// page read as one grid rather than as a row of unrelated tiles:
//
//   name + sub          how far off goal      (a tinted badge, top right)
//   the gauge           the figure, and ON/BELOW/OVER TARGET under it
//   the delta           against the prior period, in the goal's own units
//   Target / YTD*       the two rows every card has
//   an action          (primary drivers only — where the answer is)
//   the monthly line    with its own range printed
//
// A supporting metric is the same card at three quarters the size, so the
// bottom half of the page is not a different design from the top half.
//
// Nothing here computes a figure. Everything is read off the card the server
// sent, so this page and the Leadership board cannot disagree.

const FMT = { pct: fmtPct, int: fmtCount, money: fmtMoney, time: fmtMmSs };

/** A figure in its own units. */
const fmt = (kind, v) => (FMT[kind] || fmtCount)(v);

/** A magnitude, unsigned — what a delta or a gap to goal is printed as. */
function fmtAbs(kind, v) {
  const n = Math.abs(v);
  if (kind === 'pct') return `${n.toFixed(1)} pts`;
  if (kind === 'int') return Math.round(n).toLocaleString('en-US');
  if (kind === 'money') return `$${n.toFixed(2)}`;
  return fmtMmSs(n);
}

/** "+0:33 vs goal" / "−0.5 pts vs goal" — the badge in the corner. */
function goalBadge(kind, value, target) {
  if (value == null || target == null) return null;
  const diff = value - target;
  return `${diff >= 0 ? '+' : '−'}${fmtAbs(kind, diff)} vs goal`;
}

// Whether we are meeting the goal, and how that colours the card. `betterDir`
// is 'up' for service level, 'down' for everything measured in time, money or
// callers lost, and 'neutral' for volume — where there is no good direction,
// only a forecast to track against.
function statusFor(betterDir, value, target) {
  if (value == null) return { label: 'Warming', color: c.textFaint, bg: c.surfaceSunk };
  if (target == null) return { label: '', color: c.textFaint, bg: c.surfaceSunk };
  const meeting = betterDir === 'up' ? value >= target : value <= target;
  if (meeting) return { label: 'On target', color: c.successText, bg: c.successTint };
  return { label: betterDir === 'up' ? 'Below target' : 'Over target', color: c.dangerText, bg: c.dangerTint };
}

/** How far round the arc: towards the target, or under it where lower is better. */
function fillFor(betterDir, value, target) {
  if (value == null || !(target > 0)) return 0;
  const f = betterDir === 'up' ? value / target : target / Math.max(value, 1);
  return Math.max(0, Math.min(1, f));
}

// The line under the gauge: how this period compares with the one before it.
// Direction decides the colour, not the sign — an AHT that fell is green.
function deltaFor(kind, betterDir, value, prior, label) {
  if (value == null || prior == null) return null;
  const diff = value - prior;
  const tone = betterDir === 'neutral' ? c.textMuted
    : (betterDir === 'up' ? diff > 0 : diff < 0) ? c.successText : c.dangerText;
  return { text: `${diff >= 0 ? '▲' : '▼'} ${fmtAbs(kind, diff)}`, color: tone, label };
}

function Rows({ rows, dense }) {
  return (
    <div style={{ ...s.rows, ...(dense ? s.rowsDense : null) }}>
      {rows.map(([label, value]) => (
        <div key={label} style={{ ...s.row, ...(dense ? s.rowDense : null) }}>
          <span style={s.rowLabel}>{label}</span>
          <strong style={s.rowValue}>{value}</strong>
        </div>
      ))}
    </div>
  );
}

/**
 * A primary driver — service level, call volume, handle time.
 *
 * `card` is the server's card for the metric. `rowsOverride` exists for
 * volume, which is measured against a forecast rather than a target and so
 * has different rows to show.
 */
export function ExecHeroCard({
  name, sub, card: k, kind, betterDir, priorLabel, ownerLabel,
  rowsOverride, badgeOverride, statusOverride, fillOverride, deltaOverride, gapOverride,
  actionLabel, onAction, secondaryLabel, onSecondary, secondaryPressed, caption,
}) {
  const value = k?.value ?? null;
  const target = k?.target ?? null;
  const st = statusOverride ?? statusFor(betterDir, value, target);
  // Volume has no target to be above or below; what sits under its figure is
  // how far it is running from the forecast, so the gap is passed in.
  const gap = gapOverride !== undefined ? gapOverride
    : (value != null && target != null ? value - target : null);
  const badge = badgeOverride ?? (goalBadge(kind, value, target)
    ? { label: goalBadge(kind, value, target), color: st.color, bg: st.bg }
    : { label: 'No target set', color: c.textFaint, bg: c.surfaceSunk });
  const delta = deltaOverride !== undefined ? deltaOverride
    : deltaFor(kind, betterDir, value, k?.priorMonth, priorLabel);
  const rows = rowsOverride ?? [
    [`Target${ownerLabel ? ` (${ownerLabel})` : ''}`, fmt(kind, target)],
    ['YTD*', fmt(kind, k?.ytd)],
  ];

  return (
    <section style={s.hero} aria-label={name}>
      <header style={s.head}>
        <div>
          <div style={s.heroName}>{name}</div>
          <div style={s.sub}>{sub}</div>
        </div>
        <span style={{ ...s.badge, background: badge.bg, color: badge.color }}>{badge.label}</span>
      </header>

      <ExecGauge value={fmt(kind, value)} color={st.color}
        fill={fillOverride ?? fillFor(betterDir, value, target)}
        status={(st.label || 'No target').toUpperCase()}
        statusDelta={gap == null ? null : `${gap >= 0 ? '+' : '−'}${fmtAbs(kind, gap)}`}
        direction={delta?.text.charAt(0)} title={`${name} ${st.label || ''}`.trim()} />

      <div style={s.delta}>
        <strong style={{ ...s.deltaValue, color: delta?.color || c.textFaint }}>{delta?.text || '—'}</strong>
        <span style={s.deltaLabel}>{delta?.label || 'Current period'}</span>
      </div>

      <Rows rows={rows} />

      {/* Two actions on one card, as the old page's Service level card has:
          the first sends you to the pod breakdown, the second opens the day
          by day underneath. The second is a TOGGLE and says so with
          aria-pressed, because it reveals a panel on this page rather than
          taking you somewhere. */}
      {(actionLabel || secondaryLabel) && (
        <div style={s.actions}>
          {actionLabel && (
            <button type="button" onClick={onAction} style={s.action}>{actionLabel} →</button>
          )}
          {secondaryLabel && (
            <button type="button" onClick={onSecondary} aria-pressed={!!secondaryPressed}
              style={secondaryPressed ? s.actionOn : s.action}>{secondaryLabel}</button>
          )}
        </div>
      )}

      <MetricSparkline months={k?.months} target={target} betterDir={betterDir}
        fmt={(v) => fmt(kind, v)} stretch axis markers
        rangeLabel={k?.range ? `${fmt(kind, k.range.min)} – ${fmt(kind, k.range.max)}` : null} />

      {caption && <div style={s.caption}>{caption}</div>}
    </section>
  );
}

/**
 * A supporting metric. Same anatomy, three quarters the size — and `noTarget`
 * where the old app has never agreed one, which says so rather than inventing
 * a number to fail against.
 */
export function ExecStripCard({
  name, sub, card: k, kind, betterDir, priorLabel, ownerLabel, caption, noTarget = false,
}) {
  const value = k?.value ?? null;
  const target = noTarget ? null : (k?.target ?? null);
  const st = noTarget
    ? { label: 'No target set', color: c.textFaint, bg: c.surfaceSunk }
    : statusFor(betterDir, value, target);
  const gap = value != null && target != null ? value - target : null;
  const badgeText = noTarget ? 'No target set' : goalBadge(kind, value, target);
  const delta = deltaFor(kind, betterDir, value, k?.priorMonth, priorLabel);

  return (
    <section style={s.strip} aria-label={name}>
      <header style={s.stripHead}>
        <span style={s.stripName}>{name}</span>
        <span style={{ ...s.badge, ...s.badgeSm, background: st.bg, color: st.color }}>
          {noTarget && <span style={s.badgeDot} />}
          {badgeText || '—'}
        </span>
      </header>
      {sub && <div style={s.sub}>{sub}</div>}

      <ExecGauge compact value={fmt(kind, value)} color={st.color}
        fill={noTarget ? 0.7 : fillFor(betterDir, value, target)}
        status={(st.label || 'Warming').toUpperCase()}
        statusDelta={gap == null ? null : `${gap >= 0 ? '+' : '−'}${fmtAbs(kind, gap)}`}
        direction={delta?.text.charAt(0)} title={`${name} ${st.label || ''}`.trim()} />

      <div style={{ ...s.delta, ...s.deltaSm }}>
        <strong style={{ ...s.deltaValueSm, color: delta?.color || c.textFaint }}>{delta?.text || '—'}</strong>
        <span style={s.deltaLabelSm}>{delta?.label || 'Current period'}</span>
      </div>

      <Rows dense rows={[
        [`Target${ownerLabel ? ` (${ownerLabel})` : ''}`, noTarget ? 'Not set' : fmt(kind, target)],
        ['YTD*', fmt(kind, k?.ytd)],
      ]} />

      <div style={s.caption}>{caption}</div>

      <div style={s.stripFoot}>
        <MetricSparkline months={k?.months} target={target} betterDir={betterDir}
          fmt={(v) => fmt(kind, v)} stretch axis markers
          rangeLabel={k?.range ? `${fmt(kind, k.range.min)} – ${fmt(kind, k.range.max)}` : null} />
      </div>
    </section>
  );
}

const s = {
  hero:         { ...card.base, borderRadius: '14px', padding: '13px 16px 12px',
    display: 'flex', flexDirection: 'column', minWidth: 0 },
  strip:        { ...card.base, borderRadius: '14px', padding: '14px',
    display: 'flex', flexDirection: 'column', minWidth: 0 },
  head:         { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' },
  stripHead:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' },
  heroName:     { fontSize: '13px', fontWeight: 750, letterSpacing: '0.09em',
    textTransform: 'uppercase', color: c.textStrong },
  stripName:    { fontSize: '12px', fontWeight: 750, letterSpacing: '0.06em',
    textTransform: 'uppercase', color: c.textStrong },
  sub:          { marginTop: '4px', fontSize: '12px', color: c.textFaint },
  badge:        { display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px',
    fontWeight: 750, padding: '6px 10px', borderRadius: '5px', whiteSpace: 'nowrap' },
  badgeSm:      { fontSize: '10.5px', fontWeight: 700, padding: '4px 8px', borderRadius: '999px' },
  badgeDot:     { width: '6px', height: '6px', borderRadius: '50%', background: c.textFaint },
  delta:        { marginTop: '8px', minHeight: '28px', display: 'flex',
    justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' },
  deltaSm:      { marginTop: '0', minHeight: '23px' },
  deltaValue:   { fontSize: '15px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  deltaValueSm: { fontSize: '12.5px', fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  deltaLabel:   { fontSize: '12.5px', color: c.textFaint },
  deltaLabelSm: { fontSize: '11.5px', color: c.textFaint },
  rows:         { marginTop: '6px', borderTop: `1px solid ${c.border}` },
  rowsDense:    { marginTop: '0' },
  row:          { display: 'flex', justifyContent: 'space-between', padding: '6px 0',
    borderBottom: `1px solid ${c.surfaceSunk}`, fontSize: '13px' },
  rowDense:     { padding: '5px 0', fontSize: '12px' },
  rowLabel:     { color: c.textFaint },
  rowValue:     { color: c.textStrong, fontVariantNumeric: 'tabular-nums' },
  actions:      { marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' },
  action:       { border: `1px solid ${c.borderStrong}`, borderRadius: '7px',
    background: c.surface, color: c.textStrong, padding: '7px 10px', fontSize: '13px',
    fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', textAlign: 'center' },
  actionOn:     { border: `1px solid ${c.navy}`, borderRadius: '7px',
    background: c.surfaceSunk, color: c.navy, padding: '7px 10px', fontSize: '13px',
    fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', textAlign: 'center' },
  caption:      { fontSize: '11px', color: c.textFaint, lineHeight: 1.35, marginTop: '6px', minHeight: '30px' },
  stripFoot:    { marginTop: 'auto' },
};
